import { Box, InputAdornment, type SxProps, type Theme, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { CONTROL_HEIGHT, FONT_SIZE, INK, NUMERIC_FIELD_WIDTH } from "../../../theme/tokens";
import { SystemFlatSwitch } from "../Clickable/SystemFlatSwitch";
import { InfoIconWithTooltip } from "../molecules/InfoIconWithTooltip";
import { SystemValidationTextField } from "./SystemValidationTextField";

/**
 * One selectable way of expressing a rate — the pair of "what unit is this number in" and "what is a
 * legal value in that unit". The unit and the bound travel together on purpose: a percentage rate
 * caps at 100 and a flat dollar rate does not, so a caller that switches mode must not have to
 * remember to switch the validation too.
 */
export type WageModeOption<T extends string> = {
	value: T;
	/** Already-translated switch label, e.g. "Flat rate" / "Percentage". */
	label: string;
	/** Unit glyph rendered as the amount field's leading adornment while this mode is active ("$", "%"). */
	unit: string;
	/** Inclusive upper bound enforced while this mode is active. Omit for an unbounded mode (a flat rate). */
	maxValue?: number;
	/** Already-translated message shown when the amount exceeds `maxValue`. */
	maxValueHelperText?: string;
};

type Props<T extends string> = {
	/** Already-translated field label. */
	label: string;
	/** Tooltip body for the "ⓘ" beside the label. Omit for no tooltip. */
	tooltip?: ReactNode;
	/** Optional leading marker (e.g. an `AccentIconTile`) rendered before the label. */
	icon?: ReactNode;
	/**
	 * The raw input text, NOT a number: a wage is typed digit by digit, so "16." has to survive a
	 * keystroke. Round-tripping through `Number` collapses it to `16` and eats the next character, so
	 * the caller owns the string and parses it with `parseWageAmount` when it commits.
	 */
	amount: string;
	onAmountChange: (amount: string) => void;
	mode: T;
	onModeChange: (mode: T) => void;
	/** The modes offered, in switch order. `mode` must name one of them. */
	modeOptions: WageModeOption<T>[];
	/** Already-translated message shown when the text isn't a number of 0 or more. */
	invalidHelperText: string;
	/** Anything explaining the consequence of the value — a resulting-rate chip, a sentence. */
	hint?: ReactNode;
	disabled?: boolean;
	/** Amount field width (px). Defaults to the shared "number with a unit adornment" width. */
	fieldWidth?: number;
	sx?: SxProps<Theme>;
	dataTestId: string;
};

/**
 * A non-negative number, or `undefined` when the text isn't one. Exported so a caller gates its own
 * Save on exactly the rule the field validates against, rather than a second copy that can drift.
 */
export const parseWageAmount = (amount: string): number | undefined => {
	const trimmed = amount.trim();
	if (trimmed === "") return undefined;
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

/** Whether `amount` is a legal value for `option` — a non-negative number within the mode's bound. */
export const isWageAmountValid = <T extends string>(amount: string, option: WageModeOption<T>): boolean => {
	const parsed = parseWageAmount(amount);
	if (parsed === undefined) return false;
	return option.maxValue === undefined || parsed <= option.maxValue;
};

/**
 * "A rate, and the unit it is expressed in" — an amount field whose leading adornment and validation
 * bound both follow a mode switch beside it. The `$` / `%` duality appears wherever this app lets a
 * rate be either an absolute amount or a share of another number (training & orientation wages, shift
 * bonus plans, hours-rate rows), and every one of those had re-derived the adornment, the >100 check
 * and the switch-vs-field vertical alignment by hand.
 *
 * Fully prop-driven: the modes, their units, their bounds and every string come from the caller, so
 * the component owns only the presentation and the "is this legal in the current unit" check.
 */
export const WageAmountAndModeField = <T extends string>({
	label,
	tooltip,
	icon,
	amount,
	onAmountChange,
	mode,
	onModeChange,
	modeOptions,
	invalidHelperText,
	hint,
	disabled,
	fieldWidth = NUMERIC_FIELD_WIDTH.SETTINGS_WITH_UNIT,
	sx,
	dataTestId,
}: Props<T>) => {
	const activeOption = modeOptions.find((option) => option.value === mode);
	if (!activeOption) {
		// A mode with no matching option has no unit and no bound, so there is nothing legitimate to
		// render — fail loudly at the caller rather than draw a field that validates against nothing.
		throw new Error(`WageAmountAndModeField: mode "${mode}" is not in modeOptions`);
	}

	const parsed = parseWageAmount(amount);
	const exceedsMax = parsed !== undefined && activeOption.maxValue !== undefined && parsed > activeOption.maxValue;

	return (
		<Box data-testid={dataTestId} sx={[{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
				{icon}
				<Typography component="span" sx={{ fontWeight: 600, fontSize: FONT_SIZE.CAPTION, letterSpacing: "0.02em", lineHeight: 1.2, color: INK.MUTED }}>
					{label}
				</Typography>
				{tooltip !== undefined && <InfoIconWithTooltip title={tooltip} dataTestId={`${dataTestId}-tooltip`} iconSx={{ color: INK.MUTED }} />}
			</Box>

			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
				<SystemValidationTextField
					inputType="number"
					value={amount}
					onChange={(event) => onAmountChange(event.target.value)}
					disabled={disabled}
					inputProps={{ startAdornment: <InputAdornment position="start">{activeOption.unit}</InputAdornment> }}
					// The visible label is our own styled row above (it carries the icon and the tooltip), so the
					// field gets no MUI `label` and would otherwise reach a screen reader as an unnamed number
					// box — indistinguishable from the other wage field beside it. Named on the native input
					// rather than via the `label` prop, whose ARIA the component puts on the TextField root.
					injectedProps={{ slotProps: { htmlInput: { min: 0, "aria-label": label } } }}
					validation={{
						validationCallback: () => isWageAmountValid(amount, activeOption),
						helperText: { invalid: exceedsMax && activeOption.maxValueHelperText ? activeOption.maxValueHelperText : invalidHelperText },
						performValidation: "always",
						showValidWhenValid: "never",
					}}
					reserveHeightForHelperText
					formControlSx={{ width: fieldWidth }}
					dataTestId={`${dataTestId}-amount`}
				/>
				{/* Centre the switch against the field itself, not against its reserved helper-text column. */}
				<Box sx={{ display: "flex", alignItems: "center", minHeight: CONTROL_HEIGHT.STANDARD }}>
					<SystemFlatSwitch<T>
						options={modeOptions.map((option) => ({ value: option.value, label: option.label, disabled }))}
						activeIndexOrValue={mode}
						onSwitch={(option) => onModeChange(option.value)}
						delayCallback={0}
						data-testid={`${dataTestId}-mode`}
					/>
				</Box>
			</Box>

			{hint}
		</Box>
	);
};
